// Вложения владельца в чате. Описание вложения хранится в событии user_message — лента показывает миниатюры
// и карточки и после перезагрузки страницы. Байты лежат в хранилище медиа по sha256. Агент видит картинки прямо
// в сообщении (base64, уменьшенные до MODEL_EDGE), об остальных файлах получает строку с путём в своём /media
// (только чтение) — и сам решает, что с ними делать.

export type AttachmentKind = 'image' | 'video' | 'audio' | 'file';

export interface Attachment {
  /** Оригинал в хранилище: sha/ab/<sha256>.<ext> (расширение — по содержимому). */
  path: string;
  /** Имя для людей (как у владельца на устройстве). */
  name: string;
  /** Тип по содержимому файла, не по имени. */
  mime: string;
  kind: AttachmentKind;
  /** Коротко о формате: PNG, MOV, PDF, CSV… */
  format: string;
  bytes: number;
  width?: number;
  height?: number;
  durationSec?: number;
  /** Картинка, которую видит модель и которая открывается крупно (≤ MODEL_EDGE px). Нет — картинку открыть не удалось. */
  view?: string;
  viewWidth?: number;
  viewHeight?: number;
  /** Миниатюра для ленты. */
  thumb?: string;
}

/** Пределы вложений: сколько файлов в сообщении, какие размеры принимает платформа и что видит модель. */
export const ATTACH_LIMITS = {
  files: 10,
  /** Картинка на входе. */
  imageBytes: 20 * 1024 * 1024,
  /** Видео и прочие файлы — как у загрузки в библиотеку. */
  fileBytes: 1024 * 1024 * 1024,
  /** Длинная сторона картинки для модели. */
  modelEdge: 2000,
} as const;

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} Б`;
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} КБ`;
  if (n < 1024 ** 3) return `${(n / 1024 / 1024).toFixed(1)} МБ`;
  return `${(n / 1024 ** 3).toFixed(1)} ГБ`;
}

export function formatDuration(sec: number): string {
  const s = Math.max(0, Math.round(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
}

/** Строка о файле для людей: «MOV · 1080×1920 · 0:34 · 58.3 МБ». */
export function attachmentMeta(a: Attachment): string {
  return [
    a.format,
    a.width && a.height ? `${a.width}×${a.height}` : null,
    a.durationSec ? formatDuration(a.durationSec) : null,
    formatBytes(a.bytes),
  ]
    .filter(Boolean)
    .join(' · ');
}

const NOUN: Record<AttachmentKind, string> = {
  image: 'Картинка',
  video: 'Видео',
  audio: 'Звук',
  file: 'Файл',
};

/** Картинка, которую модель увидит в самом сообщении. */
export const shownToModel = (a: Attachment) => a.kind === 'image' && !!a.view;

/**
 * Что сказать агенту о вложениях — строка после текста владельца. `agentPath` — путь файла хранилища так,
 * как его видит агент (в контейнере — /media/…). `inline: false` — картинки в сообщение не вложены (задание
 * работе «разработчик» уходит текстом): агент смотрит их по пути.
 */
export function attachmentsNote(
  atts: Attachment[],
  agentPath: (rel: string) => string,
  opts: { inline?: boolean } = {},
): string {
  const inline = opts.inline ?? true;
  if (!atts.length) return '';
  const lines = atts.map((a, i) => {
    const facts = [
      a.format,
      a.width && a.height ? `${a.width}×${a.height}` : null,
      a.durationSec ? formatDuration(a.durationSec) : null,
      formatBytes(a.bytes),
    ]
      .filter(Boolean)
      .join(', ');
    let seen = '';
    if (a.kind === 'image' && !inline) seen = ' В сообщение не вложена — посмотри файл.';
    else if (shownToModel(a)) {
      const smaller =
        a.viewWidth && a.viewHeight && a.width && a.viewWidth < a.width
          ? ` (уменьшена до ${a.viewWidth}×${a.viewHeight})`
          : '';
      seen = ` Ты видишь её в этом сообщении${smaller}.`;
    } else if (a.kind === 'image') seen = ' Показать её тебе не получилось: формат не открылся.';
    return `${i + 1}. ${NOUN[a.kind]} «${a.name}» — ${facts}.${seen} Файл: ${agentPath(a.path)}`;
  });
  const hint = atts.some((a) => a.kind === 'video')
    ? '\nВидео можно положить в библиотеку футажа инструментом library_add — по этому пути.'
    : '';
  return `[Вложения владельца — его файлы; папка хранилища только для чтения:\n${lines.join('\n')}${hint}]`;
}
